import { Directive, ElementRef, HostListener, Input, forwardRef } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

@Directive({
  selector: '[appNumericInputV2]',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => NumericInputV2Directive),
      multi: true
    }
  ],
  standalone: true
})
export class NumericInputV2Directive implements ControlValueAccessor {
  private _value: number | null = null;
  private _isDisabled = false;
  private _isFocused = false;
  private _hasBeenSet = false; // Track if the field has been intentionally set
  
  // Callback functions for ControlValueAccessor
  private onChange = (value: number | null) => {};
  private onTouched = () => {};

  @Input() placeholder = '';
  @Input() maxDecimals = 2;
  @Input() showCurrency = false; // New input for currency formatting
  @Input() showDashes = true; // New input to show dashes for empty values
  @Input() showPercentage = false; // New input for percentage formatting
  @Input() allowDecimals = true; // New input to enable/disable decimal input

  constructor(private elementRef: ElementRef<HTMLInputElement>) {
    // Set initial placeholder
    this.elementRef.nativeElement.placeholder = this.placeholder;
  }

  // ControlValueAccessor implementation
  writeValue(value: number | null): void {
    this._value = value;
    // Mark as set if a value is provided, otherwise reset the flag
    // For programmatic values, we assume they are intentionally set
    this._hasBeenSet = value !== null && value !== undefined;
    this.updateDisplay();
  }

  registerOnChange(fn: (value: number | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this._isDisabled = isDisabled;
    this.elementRef.nativeElement.disabled = isDisabled;
  }

  @HostListener('input', ['$event'])
  onInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const value = input.value;

    // If user starts typing and the field had "0" (not set by user), clear the field first
    if (value.length === 1 && value === '0' && this._value === 0 && !this._hasBeenSet) {
      input.value = '';
      this._value = null;
      this._hasBeenSet = false;
      this.onChange(null);
      return;
    }

    // Filter input to allow only valid numeric characters
    const filteredValue = this.filterNumericInput(value);
    
    // Update input value if it was filtered
    if (filteredValue !== value) {
      input.value = filteredValue;
    }

    // Convert to number and emit change
    const numericValue = this.parseToNumber(filteredValue);
    
    this._value = numericValue;
    
    // Mark that the field has been set (unless it's empty)
    if (filteredValue === '' || filteredValue === null) {
      this._hasBeenSet = false;
    } else {
      this._hasBeenSet = true;
    }
    
    this.onChange(numericValue);
  }

  @HostListener('focus', ['$event'])
  onFocus(event: FocusEvent): void {
    this._isFocused = true;
    this.onTouched();
    
    // Show raw number for editing (remove formatting)
    const input = event.target as HTMLInputElement;
    
    // If the field shows dashes, clear it
    if (input.value === '--') {
      input.value = '';
    } else if (this._value !== null && this._value !== undefined) {
      input.value = this._value.toString();
    } else {
      input.value = '';
    }
  }

  @HostListener('blur', ['$event'])
  onBlur(event: FocusEvent): void {
    this._isFocused = false;
    
    // Apply international formatting for display
    this.updateDisplay();
  }

  @HostListener('keydown', ['$event'])
  onKeyDown(event: KeyboardEvent): void {
    // Allow: backspace, delete, tab, escape, enter
    if ([8, 9, 27, 13, 46].indexOf(event.keyCode) !== -1 ||
        // Allow: Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X
        (event.keyCode === 65 && event.ctrlKey) ||
        (event.keyCode === 67 && event.ctrlKey) ||
        (event.keyCode === 86 && event.ctrlKey) ||
        (event.keyCode === 88 && event.ctrlKey) ||
        // Allow: home, end, left, right
        (event.keyCode >= 35 && event.keyCode <= 39)) {
      return;
    }

    // Ensure that it is a number and stop the keypress
    if ((event.shiftKey || (event.keyCode < 48 || event.keyCode > 57)) && 
        (event.keyCode < 96 || event.keyCode > 105) && 
        (this.allowDecimals ? event.keyCode !== 190 && event.keyCode !== 110 : true)) { // Allow decimal point only if allowDecimals is true
      event.preventDefault();
    }

    // Only allow one decimal point if decimals are allowed
    if (this.allowDecimals) {
      const input = event.target as HTMLInputElement;
      if ((event.keyCode === 190 || event.keyCode === 110) && input.value.includes('.')) {
        event.preventDefault();
      }
    }
  }

  @HostListener('paste', ['$event'])
  onPaste(event: ClipboardEvent): void {
    event.preventDefault();
    
    const pastedText = event.clipboardData?.getData('text') || '';
    const filteredValue = this.filterNumericInput(pastedText);
    
    if (filteredValue) {
      const input = event.target as HTMLInputElement;
      input.value = filteredValue;
      
      const numericValue = this.parseToNumber(filteredValue);
      this._value = numericValue;
      this.onChange(numericValue);
      this.updateDisplay();
    }
  }

  private filterNumericInput(value: string): string {
    // Remove all non-numeric characters except decimal point (only if decimals are allowed)
    let filtered = this.allowDecimals ? value.replace(/[^0-9.]/g, '') : value.replace(/[^0-9]/g, '');
    
    // Ensure only one decimal point if decimals are allowed
    const decimalIndex = filtered.indexOf('.');
    if (decimalIndex !== -1 && this.allowDecimals) {
      filtered = filtered.substring(0, decimalIndex + 1) + 
                 filtered.substring(decimalIndex + 1).replace(/\./g, '');
    } else if (decimalIndex !== -1 && !this.allowDecimals) {
      // Remove decimal point if decimals are not allowed
      filtered = filtered.replace(/\./g, '');
    }
    
    // Truncate leading zeros, but preserve decimal values like 0.25
    if (decimalIndex === -1 || !this.allowDecimals) {
      // No decimal point - remove leading zeros
      filtered = filtered.replace(/^0+/, '') || '0';
    } else {
      // Has decimal point - remove leading zeros before decimal, but keep 0.25 format
      const beforeDecimal = filtered.substring(0, decimalIndex);
      const afterDecimal = filtered.substring(decimalIndex + 1);
      
      if (beforeDecimal === '0') {
        // Keep as 0.25 format
        filtered = '0.' + afterDecimal;
      } else {
        // Remove leading zeros before decimal
        const trimmedBefore = beforeDecimal.replace(/^0+/, '') || '0';
        filtered = trimmedBefore + '.' + afterDecimal;
      }
    }
    
    // Limit decimal places to maxDecimals if decimals are allowed
    if (decimalIndex !== -1 && this.allowDecimals && filtered.length > decimalIndex + this.maxDecimals + 1) {
      filtered = filtered.substring(0, decimalIndex + this.maxDecimals + 1);
    }
    
    return filtered;
  }

  private parseToNumber(value: string): number | null {
    if (!value || value === '') {
      return null;
    }
    
    // Allow 0 values now (for decimal inputs like 0.25)
    const parsed = parseFloat(value);
    return isNaN(parsed) ? null : parsed;
  }

  private updateDisplay(): void {
    const input = this.elementRef.nativeElement;
    
    if (this._isFocused) {
      // Don't format while focused (for editing)
      return;
    }
    
    if (this._value === null || this._value === undefined) {
      // Show dashes for empty/null values if showDashes is enabled
      if (this.showDashes) {
        input.value = '--';
      } else {
        input.value = '';
      }
      return;
    }
    
    // If value is 0 and showDashes is enabled, show dashes regardless of _hasBeenSet
    if (this._value === 0 && this.showDashes) {
      input.value = '--';
      return;
    }
    
    // Apply international number formatting
    input.value = this.formatForDisplay(this._value);
  }

  private formatForDisplay(value: number): string {
    let formattedValue: string;
    
    if (this.showCurrency) {
      // For currency, always show exactly 2 decimal places, except for 0
      if (value === 0) {
        formattedValue = '0';
      } else {
        formattedValue = new Intl.NumberFormat('en-US', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
          useGrouping: true
        }).format(value);
        formattedValue = `$${formattedValue}`;
      }
    } else if (this.showPercentage) {
      // For percentage, use maxDecimals setting
      formattedValue = new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 0,
        maximumFractionDigits: this.maxDecimals,
        useGrouping: true
      }).format(value);
      formattedValue = `${formattedValue}%`;
    } else {
      // For regular numbers, use maxDecimals setting
      formattedValue = new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 0,
        maximumFractionDigits: this.maxDecimals,
        useGrouping: true
      }).format(value);
    }
    
    return formattedValue;
  }

  // Public method to get raw numeric value
  public getRawValue(): number | null {
    return this._value;
  }

  // Public method to set value programmatically
  public setValue(value: number | null): void {
    this._value = value;
    this._hasBeenSet = value !== null && value !== undefined; // Mark as set if value is provided
    this.onChange(value);
    this.updateDisplay();
  }

  // Public method to reset the field state (useful for clearing/resetting forms)
  public resetField(): void {
    this._value = null;
    this._hasBeenSet = false;
    this.onChange(null);
    this.updateDisplay();
  }
}